import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import SortableList from './SortableListLocal';
import ConsiderationItem from './ConsiderationItem';
import AddItem from '../../../nucleus/add-item/add-item';
import Currency from '../../../nucleus/formats/currency';
import { createConsideration } from '../../../redux/actions/considerations-actions';
import { reorderConsiderations } from '../../../redux/actions/voucher/considerations-actions';
import { toast } from '../../../redux/actions/toasts-actions';
import REQUEST_STATE from '../../../utils/request-state';
import { selectConsiderationsCommissionAmountTotal } from '../../../redux/selectors/voucher-selectors';
import {
  buildConsiderationFromExtraction,
  getExtractedRentScheduleItems,
  getTotalConsiderationTermMonths,
  sortConsiderationsByPositionAndId,
} from '../../../utils/consideration-term-utils';

const selectVoucherConsiderations = (state) => {
  const considerations = state.voucherConsiderations?.considerations;
  return Array.isArray(considerations) ? considerations : [];
};

const selectConsiIds = state => sortConsiderationsByPositionAndId(selectVoucherConsiderations(state)).map(consideration => consideration.id);
const selectFullConsiderations = state => sortConsiderationsByPositionAndId(selectVoucherConsiderations(state));
const selectTotalTerms = state => getTotalConsiderationTermMonths(selectVoucherConsiderations(state));
const selectTotalConsiderations = state => selectVoucherConsiderations(state).reduce((acc, cur) => acc + (cur.consideration_amount || 0), 0);

/**
 * Considerations
 */
const Considerations = ({
  dealId, dealType, showValidation, extractionResults, onPageRefClick, onAcceptExtraction,
}) => {
  const [shouldCreate, setShouldCreate] = useState(true);

  const handleAccept = () => {
    if (onAcceptExtraction) onAcceptExtraction();
  };
  const considerationIds = useSelector(selectConsiIds, shallowEqual);
  const allConsiderations = useSelector(selectFullConsiderations, shallowEqual);
  const requestState = useSelector(state => state.voucherConsiderations?.state || REQUEST_STATE.INITIALIZING);
  const totalTerms = useSelector(selectTotalTerms, shallowEqual);
  const totalConsiderations = useSelector(selectTotalConsiderations, shallowEqual);
  const totalCommissions = useSelector(selectConsiderationsCommissionAmountTotal, shallowEqual);
  const dispatch = useDispatch();
  const isEarlyInvoice = useSelector(state => state.voucher.voucher?.is_early_invoice);

  const handleOrderChange = (oldIndex, newIndex) => {
    const local = [...allConsiderations];
    local.splice(newIndex, 0, local.splice(oldIndex, 1)[0]);
    const considerationsReqBody = local.map((consideration, index) => ({ id: consideration.id, position_id: (index + 1), transaction: dealId }));
    dispatch(reorderConsiderations(considerationsReqBody))
      .then(() => {
        dispatch(toast('Consideration re-ordered successfully'));
      })
      .catch((error) => {
        dispatch(toast('Failed to reorder considerations. Please try again.'));
        console.error('Reorder considerations error:');
      });
  };

  const onDeleteReorder = (id) => {
    const local = [...allConsiderations];
    // eslint-disable-next-line max-len
    const considerationsReqBody = local.filter(consideration => consideration.id !== id).map((consideration, index) => ({ ...consideration, position_id: (index + 1) }));
    dispatch(reorderConsiderations(considerationsReqBody))
      .catch((error) => {
        console.error('Reorder after delete error:');
      });
  };

  // on page load, if there are no consideration after fetching, we add one
  useEffect(() => {
    if (!considerationIds.length && requestState === REQUEST_STATE.NONE && shouldCreate) {
      setShouldCreate(false);
      dispatch(createConsideration({
        amount: null,
        commission_amount: null,
        consideration_amount: 0,
        consideration_type: dealType === 'lease' ? 'Monthly Rent' : 'Purchase Price',
        transaction: dealId,
        total_units: null,
        position_id: 1,
      }));
    } else if (requestState === REQUEST_STATE.NONE && shouldCreate && considerationIds.length) {
      setShouldCreate(false);
    }
  }, [considerationIds, considerationIds.length, dealId, dealType, dispatch, shouldCreate, requestState]);

  const rentSchedule = extractionResults && extractionResults[0]?.data_extract?.consideration_info?.rent_schedule;
  const chipPageIndex = rentSchedule?.page_index ? `${rentSchedule.page_index}*` : null;
  const extractedItems = getExtractedRentScheduleItems(rentSchedule);
  const chipRowCount = extractedItems ? `${extractedItems.length} Rows` : null;

  const annotationsData = (extractionResults && extractionResults[0]?.data_extract?.annotations) || {};
  const rentScheduleBlocks = annotationsData.consideration_info_rent_schedule;
  const isLowConfidence = (() => {
    if (!rentScheduleBlocks || Object.keys(rentScheduleBlocks).length === 0) return false;
    const scores = Object.values(rentScheduleBlocks)
      .map(b => b.text_confidence)
      .filter(s => typeof s === 'number');
    if (scores.length === 0) return false;
    const min = Math.min(...scores);
    return min < 0.9;
  })();

  // Compare extracted rent schedule against live DB rows — same pattern as leaseAbstractionField isDiff
  const isExtractionDifferentFromDB = () => {
    if (!extractedItems) return false;
    if (extractedItems.length !== allConsiderations.length) return true;
    return extractedItems.some((ei, idx) => {
      const db = allConsiderations[idx];
      if (!db) return true;
      const extracted = buildConsiderationFromExtraction(ei, dealId);
      return (
        extracted.consideration_type !== db.consideration_type ||
        Math.abs(extracted.amount - (db.amount || 0)) > 0.01 ||
        Math.abs(extracted.total_units - (db.total_units || 0)) > 0.01 ||
        Math.abs(extracted.consideration_amount - (db.consideration_amount || 0)) > 0.01
      );
    });
  };
  const isDiff = isExtractionDifferentFromDB();

  return (
    <div className="financials-section-wrapper">
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        Consideration Breakdown
        {chipPageIndex && (
          <span className={`acc-extraction-chip${isLowConfidence ? ' acc-extraction-chip--low' : ''}`}>
            <span className="acc-extraction-chip__icon" aria-hidden="true" />
            {chipRowCount && isDiff ? (
              <button
                type="button"
                className="acc-extraction-chip__accept"
                onClick={handleAccept}
                title="Accept extracted consideration data"
              >
                {chipRowCount}
              </button>
            ) : chipRowCount && (
              <span className="acc-extraction-chip__value">{chipRowCount}</span>
            )}
            <button
              type="button"
              className="acc-extraction-chip__page"
              onClick={() => onPageRefClick && onPageRefClick(parseInt(rentSchedule.page_index, 10))}
            >
              Page {chipPageIndex}
            </button>
          </span>
        )}
      </h3>
      <div className="table considerations-table">
        <div className="table-header considerations-header-row">
          <div className="table-cell">Consideration Type</div>
          <div className="table-cell">{(dealType === 'lease') ? 'Period' : 'Quantity'}</div>
          <div className="table-cell">{(dealType === 'lease') ? 'Rent($)' : 'Price($)'}</div>
          <div className="table-cell">Consideration</div>
          <div className="table-cell">Commission Rate</div>
          <div className="table-cell">Commission</div>
          <div className="table-cell" />
        </div>
        <SortableList
          className="blx-sortable-list-reverse considerations-container"
          onDrop={handleOrderChange}
        >
          { considerationIds.map(id => (
            <ConsiderationItem className="considerations-single-item" key={id} id={id} showValidation={showValidation} onDeleteReorder={onDeleteReorder} />
          ))}
        </SortableList>

        {!shouldCreate && !considerationIds.length && requestState === REQUEST_STATE.NONE && (
          <p className="error-message">
            You must add at least one consideration to this {isEarlyInvoice ? 'early invoice' : 'voucher'}
          </p>
        )}
        <div>
          <AddItem
            label="Add Consideration"
            onClick={() => dispatch(createConsideration({
              amount: null,
              commission_amount: null,
              consideration_amount: 0,
              consideration_type: dealType === 'lease' ? 'Monthly Rent' : 'Purchase Price',
              transaction: dealId,
              total_units: null,
              position_id: (allConsiderations.length + 1),
            }))}
          />
        </div>
        <div className="table-row table-footer considerations-footer-row">
          <div className="table-cell">Totals</div>
          <div className="table-cell">
            {totalTerms} Months
          </div>
          <div className="table-cell" />
          <div className="table-cell">
            <Currency
              value={totalConsiderations}
              emptyState="$0.00"
            />
          </div>
          <div className="table-cell" />
          <div className="table-cell">
            <Currency
              value={totalCommissions}
              emptyState="$0.00"
            />
          </div>
          <div className="table-cell" />
        </div>
      </div>
    </div>
  );
};

Considerations.propTypes = {
  dealId: PropTypes.number.isRequired,
  dealType: PropTypes.string.isRequired,
  showValidation: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  extractionResults: PropTypes.array,
  onPageRefClick: PropTypes.func,
  onAcceptExtraction: PropTypes.func,
};

Considerations.defaultProps = {
  extractionResults: [],
  onPageRefClick: null,
  onAcceptExtraction: null,
};

export default Considerations;

